import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * CockpitDemoState — Knowledge Controls cockpit state.
 *
 * Mock mode (retired 02-Jul-2026 per operator direction) is gone. Two
 * states remain:
 *   • empty — no live query has resolved yet. Hub reads "—", every orb
 *             reads "—". Nothing else on the page.
 *   • live  — /api/knowledge-controls/query returned. Hub reads the
 *             composite total_score with a smooth ease-out animation from
 *             the previous baseline; each orb reads its API spoke.score.
 */

const CockpitDemoCtx = createContext(null);

export const useCockpitDemo = () => {
  const ctx = useContext(CockpitDemoCtx);
  if (!ctx) {
    throw new Error('useCockpitDemo must be used inside <CockpitDemoProvider>');
  }
  return ctx;
};

const animateScore = (from, to, durationMs, onTick, onDone) => {
  const start = performance.now();
  const tick = (now) => {
    const t = Math.min(1, (now - start) / durationMs);
    // ease-out cubic
    const eased = 1 - Math.pow(1 - t, 3);
    const val = Math.round(from + (to - from) * eased);
    onTick(val);
    if (t < 1) {
      requestAnimationFrame(tick);
    } else if (onDone) {
      onDone();
    }
  };
  requestAnimationFrame(tick);
};

export const CockpitDemoProvider = ({ liveOverrides = null, children }) => {
  // Two states only (mock has been retired per operator direction 02-Jul-2026):
  //   · empty  → no live payload yet. Hub reads "—", every orb value is
  //              "—" (dimmed). Left/right rails, Recommendations, Brief,
  //              continuation field and appendix are not rendered.
  //   · live   → /api/knowledge-controls/query responded. Hub reads the
  //              real total_score, each orb reads the API's spoke.score.
  const isLive  = Boolean(liveOverrides);
  const isEmpty = !isLive;
  const baseScore = isLive ? (liveOverrides.totalScore ?? 0) : null;

  // D2_KC_New_Demo_R2 (2026-08-14) · displayScore now snaps to baseScore.
  // The tween animation was overshooting to 0 during hot-mounts and
  // showed a rogue "0/100" during the pilot demo. A hard snap gives the
  // operator a stable number to point at.
  const [displayScore, setDisplayScore] = useState(baseScore);
  const scoreRef = useRef(baseScore);

  useEffect(() => {
    if (isEmpty) {
      scoreRef.current = null;
      setDisplayScore(null);
      return;
    }
    // Snap first entry into live mode (no prior baseline yet).
    if (scoreRef.current === null || scoreRef.current === undefined) {
      scoreRef.current = baseScore;
      setDisplayScore(baseScore);
      return;
    }
    if (baseScore === scoreRef.current) return;
    // Small tween ONLY when there is a genuine before/after (a real
    // ratification lands a new API baseline). No tween on first paint.
    animateScore(scoreRef.current, baseScore, 800, (v) => {
      scoreRef.current = v;
      setDisplayScore(v);
    });
  }, [baseScore, isEmpty]);

  // D2 patch · if the state ever drifts (e.g., mid-tween interruption
  // from hot module replacement), the effective read below always
  // resolves to the current baseScore when we know it. This shields
  // the pilot demo from any rogue mid-tween value being rendered.
  const effectiveDisplayScore = isEmpty
    ? null
    : (typeof displayScore === 'number' && displayScore > 0 ? displayScore : baseScore);

  // Rec-based UI helpers are no-ops now that mock recs are off the page.
  const acceptedRecIds = useMemo(() => new Set(), []);
  const isRecAccepted = useCallback(() => false, []);
  const acceptRec = useCallback(() => {}, []);
  const acceptAll = useCallback(() => {}, []);
  const resetDemo = useCallback(() => {}, []);
  const recentlyDroppedSpoke = null;
  const targetScore = baseScore;

  const value = useMemo(() => ({
    acceptedRecIds,
    isRecAccepted,
    acceptRec,
    acceptAll,
    resetDemo,
    displayScore: effectiveDisplayScore,
    baseScore,
    targetScore,
    recentlyDroppedSpoke,
    liveOverrides,
    isLive,
    isEmpty,
  }), [acceptedRecIds, isRecAccepted, acceptRec, acceptAll, resetDemo, effectiveDisplayScore, baseScore, targetScore, recentlyDroppedSpoke, liveOverrides, isLive, isEmpty]);

  return <CockpitDemoCtx.Provider value={value}>{children}</CockpitDemoCtx.Provider>;
};
